import loadable from '@loadable/component';
import { OverlaySpinner } from '@ankr.com/ui';

import { AccountPermission } from 'modules/permissions/constants';
import { GuardAccountPermission } from 'modules/account/components/GuardAccountPermission';
import { LoggingInGuard } from 'modules/auth/components/LoggingInGuard';
import { NoSSRGuard } from 'modules/common/components/NoSSRGuard';
import { Page } from 'modules/layout/components/Page';
import { Placeholder } from 'modules/common/components/Placeholder';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useAccountStatus } from 'modules/accountStatus/hooks/useAccountStatus';
import { useAuthInstance } from 'modules/auth/hooks/useAuthInstance';
import { useJWTManagerPermissions } from 'domains/jwtToken/hooks/useJWTManagerPermissions';

const ProjectsPage = loadable(
  async () => {
    const module = await import('./ProjectsPage');

    return module.ProjectsPage;
  },
  {
    fallback: <OverlaySpinner />,
  },
);

const ProjectsPagePlaceholder = loadable(
  async () => {
    const module = await import('./components/ProjectsPagePlaceholder');

    return module.ProjectsPlaceholder;
  },
  {
    fallback: <OverlaySpinner />,
  },
);

function ProjectsPageLoader() {
  const { isAccountInitializing, permissionsInitialized } = useAccount();

  const {
    allowedJWTsCountLoading,
    hasReadAccess: hasProjectsAccess,
    isAllowedJWTsCountLoaded,
  } = useJWTManagerPermissions();
  const { initialLoading: accountStatusLoading, isFreemium } =
    useAccountStatus();

  const isLoading =
    isAccountInitializing ||
    accountStatusLoading ||
    allowedJWTsCountLoading ||
    !permissionsInitialized;

  if (isLoading) {
    return <OverlaySpinner />;
  }

  const shouldForceRedirect =
    !isFreemium && !hasProjectsAccess && isAllowedJWTsCountLoaded;

  return (
    <Placeholder hasPlaceholder={isLoading} placeholder={<OverlaySpinner />}>
      <GuardAccountPermission
        permission={AccountPermission.UsageData}
        shouldForceRedirect={shouldForceRedirect}
        shouldHideAlert
        shouldRedirect
      >
        <Placeholder
          hasPlaceholder={!hasProjectsAccess}
          placeholder={<ProjectsPagePlaceholder />}
        >
          <ProjectsPage />
        </Placeholder>
      </GuardAccountPermission>
    </Placeholder>
  );
}

// todo: refactor to remove unnecessary guards
export const ProjectsPageContainer = () => {
  const { isInitialized } = useAuthInstance();

  if (!isInitialized) {
    return <OverlaySpinner />;
  }

  return (
    <Page>
      <NoSSRGuard fallback={<ProjectsPagePlaceholder />}>
        <LoggingInGuard fallback={<ProjectsPagePlaceholder />}>
          <ProjectsPageLoader />
        </LoggingInGuard>
      </NoSSRGuard>
    </Page>
  );
};
